import { CONVERSATION_ACTIVITY_EVENTS } from '../../../services/messaging-internal-events';
import { MODEL_NAMES } from '../../../constants/index';
import { conversationHasExactContacts } from '../../../utils/messaging/conversation';
import { filterSystemAndCurrentUser } from '../../../utils/messaging/user';
import { service } from '@ember/service';
import { setOwner } from '@ember/application';

export default class WebsocketActivityHandlersConversation {
  @service messaging;
  @service messagingInternalEvents;
  @service messagingNavigation;
  @service store;

  constructor(owner) {
    setOwner(this, owner);
  }

  handleActivity(response) {
    const statusHandlers = {
      updated: payload => this.#handleUpdatedActivity(payload),
      left: payload => this.#handleUpdatedActivity(payload, false),
      read: payload => this.#handleReadActivity(payload),
      replace: payload =>
        this.messagingInternalEvents.emit(CONVERSATION_ACTIVITY_EVENTS.REPLACE, payload),
      destroyed: payload =>
        this.messagingInternalEvents.emit(CONVERSATION_ACTIVITY_EVENTS.DESTROYED, payload),
    };
    const handler = response.status ? statusHandlers[response.status] : statusHandlers.updated;
    handler(response.payload);
  }

  #handleUpdatedActivity(payload, showToast = true) {
    if (this.#isSavingMatchingDraft(payload)) {
      return;
    }

    // Flag off: do not mutate the in-memory list or store. Refresh still hides
    // inaccessible threads via the index payload.
    if (!this.messaging.groupMessagingEnabled && this.#payloadDisabled(payload)) {
      return;
    }

    const record = this.#ensureConversationInStore(payload.data.id, payload);

    if (record.disabled) {
      if (this.messaging.groupMessagingEnabled) {
        const eventPayload = {
          conversationId: record.id,
        };
        if (!showToast) {
          eventPayload.showToast = false;
        }
        this.messagingInternalEvents.emit(
          CONVERSATION_ACTIVITY_EVENTS.ACCESS_REVOKED,
          eventPayload
        );
      }
      return;
    }

    if (!this.#conversationInList(this.messaging.conversations, record.id)) {
      this.#addOrReplaceConversationRecord(this.messaging.conversations, record);
    }

    const currentHandler = this.messagingNavigation.currentConversationHandler;
    if (currentHandler?.conversation?.id === record.id && !record.inactive) {
      currentHandler.resetInactiveReasons();
    }
  }

  #isSavingMatchingDraft(payload) {
    const payloadUserIds = (payload.data?.relationships?.users?.data ?? []).map(ref =>
      String(ref.id)
    );
    if (!payloadUserIds.length) {
      return false;
    }

    const payloadIdSet = new Set(payloadUserIds);
    return this.store.peekAll(MODEL_NAMES.CONVERSATION).some(conv => {
      if (!conv.isNew || !conv.isSaving) {
        return false;
      }
      const localIds = (conv.users ?? []).map(user => String(user.id));
      return localIds.length === payloadIdSet.size && localIds.every(id => payloadIdSet.has(id));
    });
  }

  #handleReadActivity(payload) {
    const conversation = this.store.normalize(MODEL_NAMES.CONVERSATION, payload.data).data;
    this.messaging.currentProfile?.removeUnreadConversation(conversation.id);
  }

  #addOrReplaceConversationRecord(conversations, persistedConversation) {
    const persistedConversationUsers = filterSystemAndCurrentUser(
      persistedConversation.users,
      this.messaging.currentProfile?.user
    );
    const matchingUnsavedConversation = conversations.content.find(
      conv => conv.isNew && conversationHasExactContacts(conv, persistedConversationUsers)
    );

    if (matchingUnsavedConversation) {
      persistedConversation.messageDraft = matchingUnsavedConversation.messageDraft;

      const index = conversations.content.indexOf(matchingUnsavedConversation);
      conversations.replace(index, 1, [persistedConversation]);

      const currentConversation = this.messagingNavigation.currentConversationHandler?.conversation;
      if (matchingUnsavedConversation === currentConversation) {
        this.messagingNavigation.toConversation(persistedConversation);
      }
    } else {
      conversations.unshiftObjects([persistedConversation]);
    }
  }

  #conversationInList(conversations, conversationId) {
    return conversations.content.some(conv => conv.id === conversationId);
  }

  #ensureConversationInStore(conversationId, payload) {
    this.store.pushPayload(MODEL_NAMES.CONVERSATION, payload);
    return this.store.peekRecord(MODEL_NAMES.CONVERSATION, conversationId);
  }

  #payloadDisabled(payload) {
    return payload.data?.attributes?.disabled === true;
  }
}
